import React, { FC, useState } from 'react'
import { Dialog } from '@ui/overlays/dialog/dialog';

import { DialogBody } from '@ui/overlays/dialog/dialog-body'
import { TextField } from '@ui/forms/input-field/text-field/text-field';

import { useExtendedNormalizedModels } from '@player/custom/hooks/use-extended-normalized-models';
import { NormalizedModel } from '@player/types';

import { DialogFooter } from '@ui/overlays/dialog/dialog-footer';
import { Button } from '@ui/buttons/button';

import { Trans } from '@ui/i18n/trans';
import { useDialogContext } from '@ui/overlays/dialog/dialog-context';

import { ModelItem, ModelItemSkeleton } from '@player/custom/filters/ModelItem';
import { AbsoluteDateRange } from '@ui/forms/input-field/date/date-range-picker/form-date-range-picker';
import {useIsMobileMediaQuery} from '@ui/utils/hooks/is-mobile-media-query';

export interface LabelsListProps {
    selected: NormalizedModel[]
    onApply: (labels: NormalizedModel[]) => void
    onClear?: () => void
    artists?: NormalizedModel[]
    genres?: NormalizedModel[]
    dateRange: AbsoluteDateRange | null
}

export const LabelsList: FC<LabelsListProps> = ({ selected, onApply, onClear, genres, artists, dateRange }) => {
    const isMobile = useIsMobileMediaQuery()

    const [search, setSearch] = useState<string>('')
    const [list, setList] = useState<NormalizedModel[]>(selected || [])

    const { close } = useDialogContext()
    const query = useExtendedNormalizedModels('normalized-models/tag', {
        query: search,
        genres: genres?.length ? genres.map(genre => genre.id).join(',') : undefined,
        artists: artists?.length ? artists.map(artist => artist.id).join(',') : undefined,
        dateRange: dateRange ? `${dateRange.start}/${dateRange.end}` : undefined,
        perPage: isMobile ? 10 : 15,
    })

    const toggleLabel = (label: NormalizedModel) => {
        if (list.find(l => l.id === label.id)) {
            setList(list.filter(l => l.id !== label.id))
        } else {
            setList([...list, label])
        }
    }

    return (
        <Dialog>
            <DialogBody padding="p-0">
                <div className="p-10">
                    <TextField
                        inputRadius="rounded"
                        value={search}
                        size="md"
                        placeholder="Find a label..."
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {query?.isLoading ? Array.from({ length: isMobile ? 10 : 15 })?.map((_, index) => (
                    <ModelItemSkeleton
                        key={`genreLoader_${index}`}
                    />
                )) : query?.data?.results?.map(model => (
                    <ModelItem
                        key={model.id}
                        model={model}
                        selected={list}
                        onSelect={toggleLabel}
                        showKey="description"
                    />
                ))}

                <DialogFooter className="border-t" padding="p-10">
                    <Button
                        variant="flat"
                        color="paper"
                        size="xs"
                        className="ml-auto rounded-sm"
                        onClick={() => {
                            close()
                            onClear?.()

                            setList([])
                        }}
                    >
                        <Trans message="Reset" />
                    </Button>

                    <Button
                        variant="flat"
                        color="primary"
                        size="xs"
                        className="ml-auto rounded-sm"
                        onClick={() => {
                            close()
                            onApply?.(list)
                        }}
                    >
                        <Trans message="Apply" />
                    </Button>
                </DialogFooter>
            </DialogBody>
        </Dialog>
    )
}
